import React, { useState } from "react";
import { SectionCard, FieldRow } from "../scraper";

const getStatusStyle = (status) => {
    switch (status) {
        case "COMPLETED": return "bg-green-100 text-green-700 border-green-200";
        case "FAILED": return "bg-red-100 text-red-600 border-red-200";
        case "IN_PROGRESS": return "bg-blue-100 text-blue-700 border-blue-200";
        case "PENDING": return "bg-amber-100 text-amber-700 border-amber-200";
        default: return "bg-gray-100 text-gray-600 border-gray-200";
    }
};

const BadgeList = ({ items, color }) => {
    if (!items?.length) return <span className="text-gray-400 text-sm">—</span>;
    return (
        <div className="flex flex-wrap gap-1.5">
            {items.map((item) => (
                <span key={item} className={`px-2 py-0.5 rounded text-xs font-medium ${color}`}>
                    {item}
                </span>
            ))}
        </div>
    );
};

const MiniStat = ({ label, value, color = "text-gray-900" }) => (
    <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5 text-center">
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <p className={`text-xl font-bold ${color}`}>{value ?? "—"}</p>
    </div>
);

const LatestCampaignCard = ({ campaign }) => {
    const [showAllCampaigns, setShowAllCampaigns] = useState(false);
    if (!campaign) return null;
    const visibleCampaigns = showAllCampaigns ? campaign.campaign_names || [] : (campaign.campaign_names || []).slice(0, 5);
    
    return (
        <SectionCard title="Latest Outreach Email">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                {/* Left: Meta info */}
                <div className="space-y-3">
                    <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">Subject</p>
                        <p className="text-base font-semibold text-gray-900">{campaign.subject}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">

                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Segment</p>
                            <p className="text-gray-700">{campaign.segment || "—"}</p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Created</p>
                            <p className="text-gray-700">
                                {new Date(campaign.created_at).toLocaleString()}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Status</p>
                            <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold border ${getStatusStyle(campaign.status)}`}>
                                {campaign.status}
                            </span>
                        </div>
                    </div>

                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Categories</p>
                        <BadgeList
                            items={campaign.categories}
                            color="bg-blue-50 text-blue-700"
                        />
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wide mb-1.5">Sub Categories</p>
                        <BadgeList
                            items={campaign.subcategories}
                            color="bg-purple-50 text-purple-700"
                        />
                    </div>
                    <div>
                        <p className="mb-1 text-xs uppercase tracking-wide text-gray-400">Campaigns</p>
                        <div className="flex flex-wrap gap-1">
                            {visibleCampaigns.length ? (
                                visibleCampaigns.map((name) => (
                                    <span key={name} className="rounded bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">{name}</span>
                                ))
                            ) : (
                                <span className="text-sm text-gray-400">—</span>
                            )}
                        </div>

                        {(campaign.campaign_names?.length || 0) > 5 && (
                            <button
                                onClick={() => setShowAllCampaigns(!showAllCampaigns)}
                                className="mt-2 text-xs font-semibold text-indigo-600 transition-colors hover:text-indigo-800"
                            >
                             {showAllCampaigns ? "Show Less" : `+${campaign.campaign_names.length - 5} More`}
                            </button>
                        )}
                    </div>
                </div>

                {/* Right: Stats */}
                <div className="grid grid-cols-2 gap-3 content-start">
                    <MiniStat label="Recipients" value={campaign.total} />
                    <MiniStat label="Success" value={campaign.success} color="text-green-600" />
                    <MiniStat label="Failed" value={campaign.failed} color="text-red-500" />
                    <MiniStat label="Attachments" value={campaign.attachments} color="text-purple-600" />

                    {/* Delivery rate bar */}
                    {campaign.total > 0 && (
                        <div className="col-span-2 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5">
                            <div className="flex justify-between text-xs mb-1.5">
                                <span className="text-gray-500 font-medium">Delivery rate</span>
                                <span className="font-semibold text-gray-700">
                                    {Math.round((campaign.success / campaign.total) * 100)}%
                                </span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-green-500 rounded-full"
                                    style={{
                                        width: `${Math.round((campaign.success / campaign.total) * 100)}%`
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </SectionCard>
    );
};

export default LatestCampaignCard;